import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import Panel from "../../../components/Dashboard/Panel";
import StatTile from "../../../components/Dashboard/StatTile";
import DataTable from "../../../components/Dashboard/DataTable";
import PageHeader from "../../../components/Dashboard/PageHeader";
import {
  displayNameOf,
  entityName,
  fmtAmount,
  greetingFor,
} from "../../../components/Dashboard/dashboardKit";
import { Users, FileText, CreditCard, Wallet, Building2 } from "lucide-react";

const viewAll = (to) => (
  <Link to={to} className="text-xs font-semibold text-brand-600 hover:text-brand-700 hover:underline">
    View all
  </Link>
);

const AccountManagerDashboard = () => {
  const { user } = useContext(AuthContext);
  const [counts, setCounts] = useState({
    clients: null,
    vendors: null,
    receivables: null,
    payables: null,
    clientInvoices: null,
    vendorInvoices: null,
  });
  const [recentClientInvoices, setRecentClientInvoices] = useState([]);
  const [recentVendorInvoices, setRecentVendorInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const countOf = (res) =>
      res?.data?.meta?.total ?? (Array.isArray(res?.data?.data) ? res.data.data.length : 0);

    const get = (url) =>
      api.get(url).catch((err) => {
        console.error(`AccountManagerDashboard: ${url} failed`, err);
        return null;
      });

    const fetchAll = async () => {
      try {
        const [clientsRes, vendorsRes, receivablesRes, payablesRes, clientInvRes, vendorInvRes] =
          await Promise.all([
            get("/clients"),
            get("/vendors"),
            get("/project-finances/receivables"),
            get("/project-finances/payables"),
            get("/project-finances/client-invoices?limit=5"),
            get("/project-finances/vendor-invoices?limit=5"),
          ]);

        setCounts({
          clients:        clientsRes ? countOf(clientsRes) : null,
          vendors:        vendorsRes ? countOf(vendorsRes) : null,
          receivables:    receivablesRes ? countOf(receivablesRes) : null,
          payables:       payablesRes ? countOf(payablesRes) : null,
          clientInvoices: clientInvRes ? countOf(clientInvRes) : null,
          vendorInvoices: vendorInvRes ? countOf(vendorInvRes) : null,
        });

        setRecentClientInvoices((clientInvRes?.data?.data ?? []).slice(0, 5));
        setRecentVendorInvoices((vendorInvRes?.data?.data ?? []).slice(0, 5));
      } catch (err) {
        console.error("AccountManagerDashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const invoiceColumns = (kind) => [
    {
      key: "code",
      header: "Invoice",
      cell: (inv) => (
        <Link
          to={`/${kind}/invoice/${inv.id}`}
          className="text-sm font-medium text-gray-800 hover:text-brand-600"
        >
          {inv.code ?? `${kind === "client" ? "ACI" : "AVI"}${inv.id}`}
        </Link>
      ),
    },
    {
      key: "party",
      header: kind === "client" ? "Client" : "Vendor",
      cell: (inv) => {
        const party = kind === "client" ? inv.client : inv.vendor;
        const prefix = kind === "client" ? "CLT" : "VEN";
        return party ? (
          <Link
            to={`/${kind === "client" ? "clients" : "vendors"}/${party.id}`}
            className="block max-w-[11rem] truncate text-sm text-gray-500 hover:text-brand-600"
          >
            {entityName(party) ?? `${prefix}-${String(party.id).padStart(3, "0")}`}
          </Link>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );
      },
    },
    {
      key: "total",
      header: "Total",
      cell: (inv) => (
        <span className="text-sm font-medium tabular-nums text-gray-800">
          {fmtAmount(inv.total, inv.currency)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Dashboard"
        subtitle={`${greetingFor(displayNameOf(user, "Account Manager"))}. Your client and vendor accounts at a glance.`}
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <StatTile icon={Users}      label="Clients"         value={counts.clients}        tone="violet"  to="/clients"            loading={loading} />
        <StatTile icon={Building2}  label="Vendors"         value={counts.vendors}        tone="indigo"  to="/vendors"            loading={loading} />
        <StatTile icon={Wallet}     label="Receivables"     value={counts.receivables}    tone="emerald" to="/client/receivables" loading={loading} />
        <StatTile icon={CreditCard} label="Payables"        value={counts.payables}       tone="amber"   to="/vendor/payables"    loading={loading} />
        <StatTile icon={FileText}   label="Client invoices" value={counts.clientInvoices} tone="brand"   to="/client/invoices"    loading={loading} />
        <StatTile icon={FileText}   label="Vendor invoices" value={counts.vendorInvoices} tone="cyan"    to="/vendor/invoices"    loading={loading} />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Recent client invoices"
          subtitle="The five most recently issued"
          action={viewAll("/client/invoices")}
        >
          <DataTable
            loading={loading}
            rows={recentClientInvoices}
            empty="No client invoices yet."
            columns={invoiceColumns("client")}
          />
        </Panel>

        <Panel
          title="Recent vendor invoices"
          subtitle="The five most recently issued"
          action={viewAll("/vendor/invoices")}
        >
          <DataTable
            loading={loading}
            rows={recentVendorInvoices}
            empty="No vendor invoices yet."
            columns={invoiceColumns("vendor")}
          />
        </Panel>
      </div>
    </div>
  );
};

export default AccountManagerDashboard;
